import React, { useState, Fragment, useCallback, useMemo, useRef } from "react";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import {
  Grid,
  Button,
  IconButton,
  useTheme,
  Typography,
  Toolbar,
  Drawer,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  TableRow,
  Box,
  Checkbox,
  Tooltip,
} from "@mui/material";

import { Clear, FilterList, TaskAlt } from "@mui/icons-material";
import moment from "moment";
import { urlParameters } from "../HOC/CustomFunctions/urlParameters";
import { useTranslation } from "react-i18next";
import SearchTable from "../ShipmentSearch/SearchTable";
import { useHistory } from "react-router";
import { windowUrl } from "../HOC/CustomFunctions/pushUrl";
import DeliveryActionForm from "./DeliveryActionForm";
import CellLink from "../HOC/CustomComponents/CellLink";
import ShipmentRegion from "../HOC/CustomComponents/ShipmentRegion";
import { Globals } from "../HOC/Classes/Globals";
import FiltersForm from "../../Hooks/shipmentListFilters";
import { FixedTableCell } from "../HOC/CustomComponents/FixedTableCell";
import ExportShipments from "../Shipments/ExportShipments";
import { Can } from "../HOC/CustomComponents/Secured";
import PrintIcon from "../Shipments/PrintIcon";
import HFWraper from "../../Layout/WraperHeaderFooter";
import { TableCellColor } from "../HOC/CustomComponents/TableCellColor";
import useWidth from "../../Hooks/useWidth";
import PrintComponent from "../Shipments/PrintComponent";

const PREFIX = "DeliveryListActions";

const classes = {
  button: `${PREFIX}-button`,
  btnMargin: `${PREFIX}-btnMargin`,
  mainSearch: `${PREFIX}-mainSearch`,
  titles: `${PREFIX}-titles`,
  toolbar: `${PREFIX}-toolbar`,
  searchForm: `${PREFIX}-searchForm`,
  searchField: `${PREFIX}-searchField`,
  overlay: `${PREFIX}-overlay`,
  searchPadding: `${PREFIX}-searchPadding`,
  drawer: `${PREFIX}-drawer`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  content: `${PREFIX}-content`,
  contentShift: `${PREFIX}-contentShift`,
  shipmentTable_: `${PREFIX}-shipmentTable_`,
  bodyCheckbox: `bodyCheckbox`,
  shipmentTable_bodyCode: `shipmentTable_bodyCode`,
  shipmentTable_status: `shipmentTable_status`,
  shipmentTable_button: `${PREFIX}-shipmentTable_button`,
  tableRow: `${PREFIX}-tableRow`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled("div")(({ theme }) => ({
  [`& .${classes.button}`]: {
    position: "sticky",
    bottom: 0,
    zIndex: 3,
    backgroundColor: "#fff",
    marginTop: 10,
    padding: 7,
  },

  [`& .${classes.btnMargin}`]: {
    width: "100%",
  },

  [`& .${classes.mainSearch}`]: {
    margin: theme.spacing(0),
    width: "100%",
  },

  [`& .${classes.titles}`]: {
    flex: "1 1 100%",
  },

  [`& .${classes.toolbar}`]: {
    borderBottom: "1px #ccd1d6 solid",
    backgroundColor: "#f5f7f9",
    width: "100%",
    justifyContent: "space-between",
  },

  [`& .${classes.searchForm}`]: {
    overflowY: "auto",
    width: "100%",
    height: "100%",
    margin: 0,
  },

  [`& .${classes.searchField}`]: {
    padding: theme.spacing(1, 1, 0, 1),
    position: "relative",
    overflow: "hidden",
  },

  [`& .${classes.overlay}`]: {
    width: "100%",
    height: "100%",
    position: "absolute",
    backgroundColor: "#ffffffa8",
    zIndex: 2,
  },

  [`& .${classes.searchPadding}`]: {
    padding: theme.spacing(1),
  },

  //////////////////////Drawer///////////////////

  [`& .${classes.drawer}`]: {
    width: drawerWidth,
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },

  [`& .${classes.drawerPaper}`]: {
    width: drawerWidth,

    zIndex: "250",

    left: "initial",
    right: "initial",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },

  [`& .${classes.content}`]: {
    flexGrow: 1,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
  },

  [`& .${classes.contentShift}`]: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: "auto",
    width: "calc(100% - 240px)",
    [theme.breakpoints.down("sm")]: {
      margin: "auto",
      width: "auto",
    },
  },

  [`& .${classes.tableRow}`]: {
    "&:hover": {
      "& .MuiTableCell-root": {
        backgroundColor: "#f5f5f5",
      },
    },
  },

  [`& .${classes.shipmentTable_}`]: {
    [theme.breakpoints.up("sm")]: {
      zIndex: "101",
      right: "0",
    },
  },

  [`& .${classes.bodyCheckbox}`]: {
    [theme.breakpoints.up("sm")]: {
      zIndex: "100",
      backgroundColor: "#fff",
      position: "sticky",
      top: 0,
      left: 0,
      minWidth: 60,
    },
  },

  [`& .${classes.shipmentTable_bodyCode}`]: {
    [theme.breakpoints.up("sm")]: {
      zIndex: "100",
      backgroundColor: "#fff",
      position: "sticky",
      top: 0,
      left: "60px",
    },
  },
  [`& .${classes.shipmentTable_status}`]: {
    zIndex: "auto !important",
  },

  [`& .${classes.shipmentTable_button}`]: {
    [theme.breakpoints.up("sm")]: {
      zIndex: "100",
      backgroundColor: "#fff",
      position: "sticky",
      top: "0",
      right: "0",
    },
  },
}));

const drawerWidth = 240;

//*********Table Function*********

const DeliveryListActions = (props) => {
  const urlQuery = urlParameters(window.location.search);

  const FILTER_INIT_DATE = {
    lastTransactionFromDate: moment(new Date())
      .locale("en")
      .subtract(1, "month")
      .add(1, "day")
      .format("YYYY-MM-DD"),
    lastTransactionToDate: moment(new Date()).locale("en").format("YYYY-MM-DD"),
  };

  const theme = useTheme();

  const { t } = useTranslation();
  const [open, setDialogOpen] = useState(false);

  const [shipmentDetails, setShipmentDetails] = useState();

  const [tableLoad, setTableLoad] = useState(false);
  const [updateMultiple, setUpdateMultiple] = useState(false);

  const screenWidth = useWidth();

  const [drawerState, setDrawerState] = React.useState({
    top: true,
    left: screenWidth === "xs" ? false : true,
    bottom: screenWidth === "xs" ? false : true,
    right: screenWidth === "xs" ? false : true,
  });

  const shipmentList = useRef([]);

  const [rowsPerPage, setRowsPerPage] = useState(urlQuery["rowsPerPage"] ?? 20);
  const [page, setPage] = useState(urlQuery["page"] ?? 0);
  const [dateValue, setDateValue] = useState();
  const [checkedIds, setCheckedIds] = useState([]);
  const [queryVariables, setQueryVariables] = useState({
    refetch: false,
    page: 0,
  });

  const resetPage = () => setPage(0);
  const queryVariablesWithInitValue = {
    ...(queryVariables && queryVariables),
  };
  const {
    refetch,
    page: pageNum,
    ...excelFilters
  } = queryVariablesWithInitValue;

  const history = useHistory();

  const pushUrlSearch = (param) => {
    const queryParams = [];
    for (const i in param) {
      encodeURIComponent(param[i]) &&
        queryParams.push(
          encodeURIComponent(i) + "=" + encodeURIComponent(param[i])
        );
    }
    const queryString = queryParams.join("&");

    const url = history.createHref({
      pathname: "/admin/delivery-list-actions",
      search: "?" + queryString,
    });
    windowUrl(url);
    //this will not effect on history.location.search
  };

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event &&
      (event.type === "keydown" || event.type === "submit") &&
      (event.type === "submit" || event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setDrawerState({ ...drawerState, [anchor]: open });
  };
  const filterAnchor = screenWidth === "xs" ? "bottom" : "left";
  const onSubmitFunc = () => {
    filterAnchor === "bottom" &&
      setDrawerState({ ...drawerState, [filterAnchor]: false });
  };

  const handleClickOpen = () => {
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setUpdateMultiple(false);
    setCheckedIds([]);
    setShipmentDetails();
  };

  const rowActionFunc = (data) => {
    setShipmentDetails(data);
    handleClickOpen();
  };

  const updateMultipleShipment = () => {
    setUpdateMultiple(true);
    handleClickOpen();
  };

  const checkedShipmentStatus = useMemo(() => {
    let selectedShipments = shipmentList.current.filter(({ id }) =>
      checkedIds.includes(id)
    );
    const valid = selectedShipments.every(
      ({ status }) => status.code === selectedShipments[0].status.code
    );
    return { valid, shipments: selectedShipments };
  }, [checkedIds]);

  const handleChangePage = (event, newPage) => {
    pushUrlSearch({
      ...urlQuery,
      ...dateValue,
      page: newPage,
      rowsPerPage: rowsPerPage,
    });

    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    pushUrlSearch({
      ...urlQuery,
      ...dateValue,
      page: 0,
      rowsPerPage: +event.target.value,
    });

    setRowsPerPage(+event.target.value);
    resetPage();
  };

  const statusDataHandler = (data) => {
    return data && data.filter((i) => i.code === "OTD" || i.code === "PKM");
  };

  const queryVariablesHandler = (variables) => {
    const varStatus = variables?.statusCode;
    const statusCode = {
      statusCode: varStatus?.length > 0 ? varStatus : ["PKM", "OTD"],
    };
    setQueryVariables({ ...variables, ...statusCode });
  };

  const clearCheckBox = () => setCheckedIds([]);
  const user = Globals.user;
  const canShowAdminNotes = user.hasPermission('shipping.shipment.view_admin_note')


  const queryFields = [
    "id",
    "code",
    "date",
    "senderName",
    "recipientName",
    "description",
    "piecesCount",
    "refNumber",
    "totalAmount",
    "returnPiecesCount",
    "returnFees",
    "allDueFees",
    "collected",
    "adminNotes",
    {
      shipmentProducts: [
        "price",
        "quantity",
        {
          product: ["id", "name"]
        }
      ]
    },
    {
      recipientZone: ["id", "name"],
    },
    {
      recipientSubzone: ["name"],
    },
    {
      status: ["code", "name"],
    },
    {
      type: ["code", "name"],
    },
    {
      branch: ["id"],
    },
    {
      lastDeliveryAgent: ["id", "name"],
    },
    {
      paymentType: ["name", "code"],
    },
  ];
  const brifeCells = [
    "code",
    "status",
    "date",
    "senderName",
    "recipient",
    "destination",
    "packageDescription",
    "packageType",
    "paymentType",
    "pieceCount",
    "shippingAgent",
    "refNumber",
    "",
  ];

  if (canShowAdminNotes) {
    const adminNote = 'adminNotes';
    brifeCells.splice(12, 0, adminNote);
  }
  const tableBody = useCallback((shipment, index, isChecked, toggleCheck) => {
    const user = Globals.user;
    const branchId = shipment.branch.id;
    const canUpdateShipment = user.canAccessBranch(branchId);

    shipmentList.current = [...shipmentList.current, shipment];
    return (
      <TableRow className={classes.tableRow} hover key={index}>
        <FixedTableCell className={classes.bodyCheckbox}>
          <Checkbox
            edge="start"
            checked={isChecked(shipment.id)}
            onChange={(e) => toggleCheck(e, shipment.id)}
            disableRipple
          />
        </FixedTableCell>
        <CellLink
          className={classes.shipmentTable_bodyCode}
          pathname={`/admin/shipments/${shipment.id}`}
        >
          {shipment.code}
        </CellLink>
        <TableCellColor
          className={classes.shipmentTable_status}
          code={shipment.status.code}
        >
          {shipment.status.name}
        </TableCellColor>
        <FixedTableCell>{moment(shipment.date).format("ll")}</FixedTableCell>
        <FixedTableCell>{shipment.senderName}</FixedTableCell>
        <FixedTableCell>{shipment.recipientName}</FixedTableCell>
        <ShipmentRegion
          pathname={`/admin/zone/${shipment.recipientZone.id}`}
          zone={shipment.recipientZone?.name}
          subzone={shipment.recipientSubzone.name}
        />
        <FixedTableCell>{shipment.description}</FixedTableCell>
        <FixedTableCell>{shipment?.type?.name}</FixedTableCell>
        <FixedTableCell>{shipment?.paymentType?.name}</FixedTableCell>
        <FixedTableCell>{shipment?.piecesCount}</FixedTableCell>
        <CellLink
          pathname={`/admin/delivery-agents/${shipment.lastDeliveryAgent?.id}`}
        >
          {shipment.lastDeliveryAgent?.name}
        </CellLink>
        <FixedTableCell>{shipment?.refNumber}</FixedTableCell>

        <Can showException permission="shipping.shipment.view_admin_note">
          <FixedTableCell>{shipment?.adminNotes}</FixedTableCell>
        </Can>

        <FixedTableCell
          className={classes.shipmentTable_button}
          component="th"
          scope="row"
        >
          <Button
            color="primary"
            variant="outlined"
            size="small"
            disabled={!canUpdateShipment}
            onClick={() => rowActionFunc(shipment)}
            startIcon={<Icon>task_alt</Icon>}
          >
            {t("update")}
          </Button>
        </FixedTableCell>
      </TableRow>
    );
  }, []);

  return (
    <Root>
      <Drawer
        className={clsx(classes.drawer)}
        variant="persistent"
        classes={{
          paper: classes.drawerPaper,
        }}
        anchor={filterAnchor}
        open={drawerState[filterAnchor]}
        onClose={() => false}
        dir={theme.direction}
      >
        <HFWraper />
        <Toolbar
          variant="dense"
          className={classes.toolbar}
          sx={{ marginBottom: 1 }}
        >
          <Typography
            className={classes.titles}
            color="inherit"
            variant="h6"
            component="div"
          >
            {t("search")}
          </Typography>
          <IconButton
            aria-label="done"
            size="small"
            onClick={toggleDrawer(filterAnchor, false)}
            color={"primary"}
          >
            <Clear />
          </IconButton>
        </Toolbar>

        <FiltersForm
          {...{ onSubmitFunc, resetPage, rowsPerPage, loading: tableLoad }}
          queryVariables={queryVariablesHandler}
          statusDataHandler={statusDataHandler}
          // dateValue={(date) => setDateValue(date)}
          initLastTransaction={FILTER_INIT_DATE}
          pathname="delivery-list-actions"
          filtersAllowed={[
            "dlvAtemp",
            "status",
            "branch",
            "originBranch",
            "businessType",
            "custm",
            "dlvAgent",
            "zone",
            "subZone",
            "refNumber",
            "service",
            "packageType",
            "paymentType",
          ]}
          clearCheckBox={clearCheckBox}
        />
      </Drawer>

      <Grid
        container
        justifyContent="center"
        spacing={3}
        className={clsx(classes.content, classes.mainSearch, {
          [classes.contentShift]: drawerState[filterAnchor],
        })}
      >
        {/* *******Table******* */}

        <Toolbar className={classes.toolbar} variant="dense">
          <Typography variant="h6">{t("outToDeliver")}</Typography>
          <Box>
            <IconButton onClick={toggleDrawer(filterAnchor, true)} size="large">
              <FilterList fontSize="inherit" />
            </IconButton>
            <ExportShipments
              disabled={checkedIds.length > 0}
              filters={excelFilters}
            />

            <Can showException permission="shipping.shipment.print">
              <PrintComponent shipmentsId={checkedIds} />
            </Can>
            <IconButton
              disabled={checkedIds.length === 0 || !checkedShipmentStatus.valid}
              onClick={updateMultipleShipment}
              size="large"
              color="primary"
            >
              <Tooltip title={t("updateMultipleShipmants")}>
                <TaskAlt fontSize="inherit" />
              </Tooltip>
            </IconButton>
          </Box>
        </Toolbar>

        <SearchTable
          {...props}
          onPageChange={handleChangePage}
          onChangeRows={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          queryVariables={queryVariablesWithInitValue}
          loading={(param) => setTableLoad(param)}
          brife
          className={classes}
          sum={false}
          tableHeadCell={brifeCells}
          tableBody={tableBody}
          notifyOnNetworkStatusChange={false}
          queryFields={queryFields}
          setCheckedIds={(ids) => setCheckedIds(ids)}
          checkedIds={checkedIds}
          withCheckAll
        />
      </Grid>
      <Dialog
        maxWidth={"sm"}
        fullWidth={true}
        open={open}
        onClose={handleClose}
      >
        <DialogTitle>{t("updateShipmentStatus")}</DialogTitle>
        <DialogContent>
          <DeliveryActionForm
            closeDialog={handleClose}
            shipment={shipmentDetails}
            queryFields={queryFields}
            shipmentsId={checkedIds}
            updateMultiple={updateMultiple}
            listShipments={checkedShipmentStatus.shipments}
          />
        </DialogContent>
      </Dialog>
    </Root>
  );
};
export default DeliveryListActions;
